import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';
import { setAuthToken } from '@/lib/api-client';
import {
  getActiveStudentId,
  hasActiveDeviceSession,
  listPairedStudents,
} from '@/features/student-auth/deviceAuth';
import { signInKid } from '@/features/student-auth/signInStudent';
import { useEffect } from 'react';

export function ProtectedRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);

  // Set auth token on mount and when it changes
  useEffect(() => {
    if (token) {
      setAuthToken(token);
    }
  }, [token]);

  // Whenever a paired-kid device is active, force authStore to reflect that
  // kid. This is the source of truth for who is signed in on this device —
  // covers reloads, direct URL entry, cases where signInKid didn't fire in
  // the pair/pick flow, and stale parent data from a previous session.
  useEffect(() => {
    const activeId = getActiveStudentId();
    if (!activeId) return;
    if (user?.id === activeId && user?.roles?.includes('student')) return;
    const paired = listPairedStudents().find((s) => s.id === activeId);
    if (paired) {
      signInKid(paired);
    }
  }, [user?.id]);

  // Allow either a parent JWT session or a paired-kid device session.
  if (isAuthenticated || hasActiveDeviceSession()) {
    return <Outlet />;
  }

  // Not authenticated. If this device has paired kids but no active one,
  // send them to the chooser rather than the login screen.
  if (listPairedStudents().length > 0) {
    return <Navigate to="/kid" replace />;
  }

  return <Navigate to="/login" replace />;
}
